import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  githubAuthApiRef,
  SessionState,
  useApi,
} from '@backstage/core-plugin-api';
import { scmAuthApiRef } from '@backstage/integration-react';
import {
  AccessProblem,
  GithubAccess,
  GithubTokenSource,
  RepoAccessError,
  tokenWithRepoAccess,
} from './githubOrgAccess';

export type GithubAccessState = 'idle' | 'checking' | 'ok' | 'error';

/**
 * Raised instead of prompting, on the silent path. Never surfaced to the
 * user: no GitHub session means "not connected yet", not a failure.
 */
class NoSessionError extends Error {}

export interface UseGithubAccessOptions {
  /**
   * Every repository (`owner/name`) and organization (`owner`) the run will
   * touch, in the order it will touch them. Compared by content, so the array
   * does not have to be memoised; entries may not contain spaces.
   */
  targets: string[];
  /** OAuth scopes the token must hold. Defaults to `['repo']`. */
  scopes?: string[];
  /**
   * Resolve tokens through scmAuth for this URL rather than through githubAuth
   * directly. Scaffolder fields pass the target repo's URL so that scmAuth
   * picks the integration; everything else can leave it unset.
   */
  scmUrl?: string;
  /** Probe on mount, prompting for GitHub access if there is no session. */
  auto?: boolean;
  /**
   * Probe silently whenever a GitHub session exists — including one another
   * field in the same form connected. Never prompts.
   */
  probeOnSignIn?: boolean;
  /** Called with every token that passed every probe. */
  onToken?: (token: string) => void;
}

export interface AuthorizeOptions {
  /**
   * Drop the GitHub session first, so the probe runs against a newly minted
   * token. Required after an `org-auth` problem: an OAuth token is authorized
   * for a SAML organization only at the moment it is created, so re-probing
   * the existing one keeps failing however many times the user retries.
   */
  fresh?: boolean;
}

export interface UseGithubAccessResult {
  state: GithubAccessState;
  /** What the token turned out to be able to do, once `state` is `ok`. */
  access: GithubAccess[];
  /** User-facing explanation of why access failed, and how to fix it. */
  error?: string;
  /** What kind of problem it was, for callers that offer a tailored fix. */
  problem?: AccessProblem;
  /** GitHub's own URL for the user to fix it, when there is one. */
  authorizeUrl?: string;
  /**
   * Re-probe, prompting for GitHub access as needed. Resolves with a token
   * that passed every probe; rejects with the user-facing error otherwise.
   */
  authorize: (options?: AuthorizeOptions) => Promise<string>;
}

/**
 * A GitHub token proven to work against every target a run will touch.
 *
 * SSO authorization is granted per organization and per token, and the create
 * flow touches more than one organization, so a single probe of the gitops
 * repo is not enough — every target is probed, in run order, so the first
 * problem reported is the first one the user has to fix.
 *
 * Re-probes whenever `targets` change, which is what makes switching the
 * target organization mid-form safe: the previous "authorized" answer was
 * about a different organization.
 */
export function useGithubAccess(
  options: UseGithubAccessOptions,
): UseGithubAccessResult {
  const {
    scopes = ['repo'],
    scmUrl,
    auto = false,
    probeOnSignIn = false,
    onToken,
  } = options;

  const githubAuth = useApi(githubAuthApiRef);
  const scmAuthApi = useApi(scmAuthApiRef);

  // Depend on content, not identity, so a caller that rebuilds the array every
  // render does not re-probe every render.
  const targetKey = options.targets.filter(Boolean).join(' ');
  const scopeKey = scopes.join(' ');
  const targets = useMemo(
    () => targetKey.split(' ').filter(Boolean),
    [targetKey],
  );
  const scopeList = useMemo(
    () => scopeKey.split(' ').filter(Boolean),
    [scopeKey],
  );

  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  // Serialises probes. `fresh` signs out, which makes sessionState$ emit, which
  // would start a second probe on top of the one doing the signing out — two
  // token requests racing, and whichever lands last decides what the user sees.
  const inFlight = useRef(false);
  const [state, setState] = useState<GithubAccessState>('idle');
  const [access, setAccess] = useState<GithubAccess[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [problem, setProblem] = useState<AccessProblem | undefined>();
  const [authorizeUrl, setAuthorizeUrl] = useState<string | undefined>();
  const stateRef = useRef<GithubAccessState>('idle');
  stateRef.current = state;

  const tokenSource = useCallback(
    (silent: boolean): GithubTokenSource => ({
      getAccessToken: async requested => {
        if (silent) {
          const token = await githubAuth.getAccessToken(requested, {
            optional: true,
          });
          if (!token) throw new NoSessionError();
          return token;
        }
        if (scmUrl) {
          const { token } = await scmAuthApi.getCredentials({
            url: scmUrl,
            additionalScope: { customScopes: { github: requested } },
          });
          return token;
        }
        return githubAuth.getAccessToken(requested);
      },
      // Dropping the session is how a lesser-scoped one gets fixed, but only
      // ever with the user watching — silently signing them out would leave
      // the form looking connected while it no longer is.
      signOut: silent
        ? () => Promise.reject(new NoSessionError())
        : () => githubAuth.signOut(),
    }),
    [githubAuth, scmAuthApi, scmUrl],
  );

  const probe = useCallback(
    async (silent: boolean, fresh = false): Promise<string> => {
      if (targets.length === 0) throw new NoSessionError();
      // A silent probe never overrides work already under way, and never
      // re-litigates an answer the user has already been given.
      if (silent && (inFlight.current || stateRef.current !== 'idle')) {
        throw new NoSessionError();
      }
      inFlight.current = true;
      if (!silent) {
        setState('checking');
        setError(undefined);
        setProblem(undefined);
        setAuthorizeUrl(undefined);
      }
      try {
        // A fresh token is the whole point after an `org-auth` problem: the
        // existing one was minted without the organization's SSO session and
        // cannot be repaired, only replaced.
        if (fresh && !silent) await githubAuth.signOut();
        const result = await tokenWithRepoAccess(
          tokenSource(silent),
          targets,
          scopeList,
        );
        setAccess(result.access);
        setError(undefined);
        setProblem(undefined);
        setAuthorizeUrl(undefined);
        setState('ok');
        onTokenRef.current?.(result.token);
        return result.token;
      } catch (e) {
        // Silent probes leave the UI alone unless they found a real problem:
        // "no session yet" is the normal state before the user connects.
        if (e instanceof NoSessionError) throw e;
        const found = e instanceof RepoAccessError ? e.problem : undefined;
        setAccess([]);
        setError((e as Error).message);
        setProblem(found);
        setAuthorizeUrl(
          found && 'authorizeUrl' in found ? found.authorizeUrl : undefined,
        );
        setState('error');
        throw e;
      } finally {
        inFlight.current = false;
      }
    },
    [githubAuth, tokenSource, targets, scopeList],
  );

  const authorize = useCallback(
    (opts?: AuthorizeOptions) => probe(false, opts?.fresh),
    [probe],
  );

  useEffect(() => {
    if (!auto) return;
    probe(false).catch(() => undefined);
  }, [auto, probe]);

  useEffect(() => {
    if (!probeOnSignIn) return undefined;
    // sessionState$ replays the current state, so this also fires for a
    // session that already existed, and again whenever `targets` change.
    const sub = githubAuth.sessionState$().subscribe(session => {
      if (session !== SessionState.SignedIn) return;
      probe(true).catch(() => undefined);
    });
    return () => sub.unsubscribe();
  }, [githubAuth, probeOnSignIn, probe]);

  return { state, access, error, problem, authorizeUrl, authorize };
}
